// src/containers/Receptions/ReceptionList/hooks/useReceptionContext.ts
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { appState } from "$reducers";
import { Services } from "$utils/typeConstants";
import useAuth from "$hooks/useAuth";
import useTabs from "$hooks/useTabs";
import ReceptionStatus from "$types/ReceptionStatus";

// This hook contains shared logic and configuration that both useReceptionList and useReceptionFilters need
const useReceptionContext = () => {
  const { currentTab } = useTabs();
  const { isAdmin, user, token, isContributor, isClient } = useAuth();
  const { filter: sidebarCompanyFilter } = useSelector(appState);

  // Configure default filters based on role
  const defaultFilterOrders = useMemo(() => {
    if (isContributor() && !isAdmin()) {
      if (isClient()) {
        const nextFilters = {
          $or: [
            {
              // CLIENT ROLE !
              company: sidebarCompanyFilter?._id,
            },
            {
              // CONTRIBUTOR ROLE : we want to see all order with OUR warehouses ...
              warehouse: {
                $in: [], // Will be populated with contributor warehouses
              },
              // ... But only if the order is for a company I'm a contributor of :
              "company][$in": [
                ...(user?.userInfo?.userCompany?.contributorOf || []),
                sidebarCompanyFilter?._id,
              ],
            },
          ],
        };
        return nextFilters;
      }

      // ONLY CONTRIBUTOR : we want to see all order with OUR warehouses ...
      const nextFilters = {
        warehouse: {
          $in: [], // Will be populated with contributor warehouses
        },
        "company][$in": [
          ...(user?.userInfo?.userCompany?.contributorOf || []),
          sidebarCompanyFilter?._id,
        ],
      };
      return nextFilters;
    }

    // Not contributor : go back to the "default" behaviour : Use the company filter (if filled)
    return {
      company: sidebarCompanyFilter?._id,
      warehouse: undefined,
    };
  }, [
    isContributor,
    isAdmin,
    sidebarCompanyFilter?._id,
    isClient,
    user?.userInfo?.userCompany?.contributorOf,
  ]);

  // Define default options for list fetching
  const defaultOptions = useMemo(
    () => ({
      defaultSort: "id:desc",
      cachePrefix: "receptionList",
      populate: [
        "orders",
        "productQuantity",
        "orders.suborders",
        "orders.suborders.suborderProducts",
        "receptions",
        "orders.files",
        "files",
        "receptionReferences.orders",
        "receptionReferences.orders.suborders",
        "receptionReferences.orders.suborders.suborderProducts",
      ],
      // As a contributor we need to ignore the SideBarFilter which is by default set to his company
      ignoreSideBarFilter: true,
      defaultFilters: {
        ...(currentTab ? { is_transfer: true } : { is_transfer: false }),
        ...defaultFilterOrders,
        ...(isAdmin() ? {} : { status: ReceptionStatus.DONE }),
      },
      jwt: token,
    }),
    [currentTab, defaultFilterOrders, isAdmin, token]
  );

  // Return the API service type to use
  const getServiceType = () => Services.GATEWAY_API;

  const getEndpoint = () => "api/order/receptions/consolidated";

  return {
    currentTab,
    defaultOptions,
    getServiceType,
    isAdmin,
    getEndpoint,
  };
};

export default useReceptionContext;
